"use client";

import React, { useEffect, useState, useCallback, useLayoutEffect } from "react";
import Link from "next/link";
import CreditsDisplay from "@/components/CreditsDisplay";
import ButtonAccount from "@/components/ButtonAccount";
import DashboardHeader from "@/components/headers/DashboardHeader";
import LoadingSpinner from "@/components/LoadingSpinner";
import { toolsRegistry } from "@/contexts/ToolsContext";
import { notFound } from "next/navigation";
import ToolsSidebarV2 from "@/app/editor-v2/_components/ToolsSidebarV2";

interface PfpEditorPageProps {
  params: {
    toolId: string;
  };
}

// Map tools to their corresponding iframe panel parameters
const toolPanelMap: Record<string, { panel: string; category?: string }> = {
  "pfpmaker-legacy": { panel: "templates" },
  "remove-background": { panel: "background" },
  "professional-backgrounds": { panel: "background", category: "professional" },
  "creative-backgrounds": { panel: "background", category: "creative" },
  "color-gradient-backgrounds": { panel: "background", category: "gradient" },
  "borders": { panel: "border" },
  "text": { panel: "text" },
  "effects": { panel: "layers" },
  "decals": { panel: "decals" },
};

export default function PfpEditorPage({ params }: PfpEditorPageProps) {
  const { toolId } = params;
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // Find tool info
  const toolInfo = toolsRegistry.find(tool => tool.id === toolId);

  if (!toolInfo || toolInfo.toolType !== 'free-editor') {
    notFound();
  }

  // Get the panel parameters for this tool
  const toolConfig = toolPanelMap[toolId] || { panel: "background" };
  const panel = toolConfig.panel;
  const backgroundCategory = toolConfig.category ? `&backgroundCategory=${toolConfig.category}` : '';

  // Use ref to store iframe URL - only regenerate when params change
  const urlCacheKey = `${toolId}-${panel}-${toolConfig.category || 'none'}`;
  const lastCacheKey = React.useRef('');
  const iframeUrlRef = React.useRef('');

  // Generate NEW URL only when tool/panel/category actually changes
  if (lastCacheKey.current !== urlCacheKey) {
    lastCacheKey.current = urlCacheKey;
    const cacheBuster = `${urlCacheKey}-${Date.now()}`;
    iframeUrlRef.current = toolId === "pfpmaker-legacy"
      ? `https://editor-embedded.pfpmaker.com/?v=${cacheBuster}`
      : `https://editor-embedded.pfpmaker.com/editor/?panel=${panel}${backgroundCategory}&dummy=false${uploadPending ? '&upload=pending' : ''}&v=${cacheBuster}`;
  }

  const iframeUrl = iframeUrlRef.current;

  // Handle iframe messages
  const handleMessage = useCallback((event: MessageEvent) => {
    // Only accept messages from the iframe domain
    if (!event.origin.includes('editor-embedded.pfpmaker.com')) {
      return;
    }

    const data = event.data;

    switch (data.type) {
      case 'iframe-ready':
        setIsIframeReady(true);
        console.log('✅ Iframe ready');
        break;

      case 'upload-complete':
        setUploadPending(false);
        if (data.success) {
          console.log('✅ Upload successful:', data.fileName);
        } else {
          console.log('❌ Upload failed:', data.error);
        }
        break;

      default:
        console.log('📩 Received iframe message:', data);
    }
  }, []);

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Reset iframe ready state when tool/params change
  useEffect(() => {
    setIsIframeReady(false);
  }, [toolId, panel, toolConfig.category]);

  // Handle file upload
  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const imageData = e.target?.result as string;

      // Send image to iframe
      const iframe = document.getElementById('editor-iframe') as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'upload-image-direct',
          imageData: imageData,
          fileName: file.name,
          targetPanel: panel
        }, '*');
      }
    };

    reader.readAsDataURL(file);
  }, [panel]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));

    if (imageFile) {
      setUploadPending(true);
      handleFileUpload(imageFile);
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  return (
    <div className="editor-app-height flex flex-col overflow-hidden bg-gray-50">
       {/* Header */}
       <DashboardHeader variant="editor" title="Free PFP Editor" hideCredits />

      {/* File Upload Zone - Only show when upload is pending */}
      {uploadPending && (
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <div className="max-w-full mx-auto px-2 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-blue-700">
                📸 Ready to upload! Drop an image file anywhere or use the upload button in the editor.
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex-1 min-h-0 grid grid-rows-[minmax(0,1fr)_auto] md:grid-rows-1 md:grid-cols-[80px_minmax(0,1fr)] ${
          toolId === "pfpmaker-legacy" ? "overflow-visible" : "overflow-hidden"
        }`}
      >
        {/* Main Editor Iframe */}
        <div
          className="min-h-0 bg-white row-start-1 md:row-start-1 md:col-start-2 flex flex-col"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <iframe
            key={`iframe-${toolId}-${panel}-${toolConfig.category || 'none'}`}
            id="editor-iframe"
            src={iframeUrl}
            frameBorder="0"
            scrolling="yes"
            style={{ overflow: "auto" }}
            className="block w-full flex-1 min-h-0"
            title={`${toolInfo.title} Editor`}
            allow="clipboard-write"
          />
        </div>

        <ToolsSidebarV2 currentToolId={toolId} dockMobileToBottom className="row-start-2 md:row-start-1 md:col-start-1" />
      </div>

      {/* Loading overlay */}
      {!isIframeReady && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
          <LoadingSpinner size="lg" text={`Loading ${toolInfo.title}`} fullScreen={false} />
        </div>
      )}
    </div>
  );
}
